import React, { useCallback, useEffect, useState } from "react";
import { Button, Card, Col, Form, Row } from "react-bootstrap";
import { Controller, Resolver, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";
import BreadcrumbItem from "../../../shared/components/BreadcrumbItem";
import { useTranslation } from "react-i18next";
import { enqueueSnackbar } from "notistack";
import { handleError } from "../../../shared/functions/handleError";
import { GroupService } from "../service";
import { IGroup, IGroupCreate } from "../../../shared/interfaces/group/IGroup";
import { UserService } from "../../users/service";
import { InstitutionService } from "../../institutions/service";
import { IUser } from "../../../shared/interfaces/user/IUser";
import { IInstitution } from "../../../shared/interfaces/institution/IInstitution";
import { ISelectOption } from "../../../shared/interfaces/shared/ISelectOption";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import debounce from "lodash/debounce";

const groupSchema = yup.object().shape({
    name: yup.string().required("validation.required").min(2, "validation.minLength").trim(),
    userIds: yup.array().of(yup.number().required()).min(1, "validation.required").required("validation.required"),
    institutionIds: yup.array().of(yup.number().required()).min(1, "validation.required").required("validation.required"),
});

interface IGroupFormValues {
    name: string;
    userIds: number[];
    institutionIds: number[];
}

const initialValues: IGroupFormValues = {
    name: "",
    userIds: [],
    institutionIds: [],
};

const customStyles = {
    control: (base: any, state: any) => ({
        ...base,
        borderRadius: "8px",
        padding: "0.35rem 0",
        borderColor: state.isFocused ? "#dbe0e5" : "#dbe0e5",
        boxShadow: state.isFocused ? "0 0 0 0.2rem rgba(147, 7, 31, 0.25)" : "none",
        "&:hover": {
            borderColor: "#d21034",
        },
    }),
    multiValue: (styles: any) => ({
        ...styles,
        backgroundColor: "#d21034",
        borderRadius: "8px",
        padding: "2px 8px",
    }),
    multiValueLabel: (styles: any) => ({
        ...styles,
        backgroundColor: "#d21034",
        color: "white",
        borderRadius: "8px",
    }),
    multiValueRemove: (styles: any) => ({
        ...styles,
        color: "white",
        backgroundColor: "#d21034",
        borderRadius: "8px",
        ":hover": {
            backgroundColor: "#93071f",
            color: "white",
        },
    }),
    menu: (base: any) => ({
        ...base,
        zIndex: 9999,
    }),
    menuPortal: (base: any) => ({
        ...base,
        zIndex: 9999,
    }),
};

const GroupForm = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { groupId } = useParams();
    const [defaultUserOptions, setDefaultUserOptions] = useState<ISelectOption<number>[]>([]);
    const [defaultInstitutionOptions, setDefaultInstitutionOptions] = useState<ISelectOption<number>[]>([]);

    const {
        control,
        handleSubmit,
        formState: { errors, isSubmitting },
        reset,
    } = useForm<IGroupFormValues>({
        resolver: yupResolver(groupSchema) as unknown as Resolver<IGroupFormValues>,
        defaultValues: async () => {
            if (groupId) {
                try {
                    const response = await GroupService.getSingle(Number(groupId));
                    const group = response?.data;
                    return {
                        name: group.name,
                        userIds: group.users?.map((u) => u.id) || [],
                        institutionIds: group.institutions?.map((i) => i.id) || [],
                    };
                } catch (e) {
                    handleError(e);
                    return initialValues;
                }
            }
            return initialValues;
        },
    });

    const loadUsers = useCallback(async (inputValue: string): Promise<ISelectOption<number>[]> => {
        try {
            const response = await UserService.getAll({
                page: 0,
                size: 50,
                firstName: inputValue || undefined,
                roles: ["POLICE", "HEALTH", "FIRE"],
            });
            return (response?.data || [])
                .filter((user: IUser) => user.id !== undefined)
                .map((user: IUser) => ({
                    value: user.id as number,
                    label: `${user.firstName} ${user.lastName} (${user.email})`,
                }));
        } catch (error) {
            handleError(error);
            return [];
        }
    }, []);

    const loadInstitutions = useCallback(async (inputValue: string): Promise<ISelectOption<number>[]> => {
        try {
            const response = await InstitutionService.getAll({
                page: 0,
                size: 50,
                name: inputValue || undefined,
            });
            return (response?.data || [])
                .filter((inst: IInstitution) => inst.id !== undefined)
                .map((inst: IInstitution) => ({
                    value: inst.id as number,
                    label: inst.name,
                }));
        } catch (error) {
            handleError(error);
            return [];
        }
    }, []);

    const debouncedLoadUsers = useCallback(
        debounce((inputValue: string, callback: (options: ISelectOption<number>[]) => void) => {
            loadUsers(inputValue).then(callback);
        }, 300),
        [loadUsers],
    );

    const debouncedLoadInstitutions = useCallback(
        debounce((inputValue: string, callback: (options: ISelectOption<number>[]) => void) => {
            loadInstitutions(inputValue).then(callback);
        }, 300),
        [loadInstitutions],
    );

    useEffect(() => {
        loadUsers("").then(setDefaultUserOptions);
        loadInstitutions("").then(setDefaultInstitutionOptions);
    }, [loadUsers, loadInstitutions]);

    // When editing, preload selected user/institution options
    useEffect(() => {
        if (groupId) {
            GroupService.getSingle(Number(groupId))
                .then((response) => {
                    const group = response?.data;
                    if (group?.users) {
                        const userOpts = group.users.map((u) => ({
                            value: u.id,
                            label: `${u.firstName} ${u.lastName} (${u.email})`,
                        }));
                        setDefaultUserOptions((prev) => {
                            const merged = [...prev];
                            userOpts.forEach((opt) => {
                                if (!merged.find((m) => m.value === opt.value)) {
                                    merged.push(opt);
                                }
                            });
                            return merged;
                        });
                    }
                    if (group?.institutions) {
                        const instOpts = group.institutions.map((i) => ({
                            value: i.id,
                            label: i.name,
                        }));
                        setDefaultInstitutionOptions((prev) => {
                            const merged = [...prev];
                            instOpts.forEach((opt) => {
                                if (!merged.find((m) => m.value === opt.value)) {
                                    merged.push(opt);
                                }
                            });
                            return merged;
                        });
                    }
                })
                .catch(handleError);
        }
    }, [groupId]);

    const onSubmit = async (data: IGroupFormValues) => {
        const payload: IGroupCreate = {
            name: data.name,
            userIds: data.userIds,
            institutionIds: data.institutionIds,
        };

        const isUpdate = !!groupId;
        const action = isUpdate
            ? GroupService.update(payload, Number(groupId))
            : GroupService.create(payload);

        action
            .then(() => {
                navigate(-1);
                reset();
                enqueueSnackbar(
                    isUpdate
                        ? t("toasts.successfullyUpdated", { name: data.name })
                        : t("toasts.successfullyCreated", { name: data.name }),
                    { variant: "success" },
                );
            })
            .catch(handleError);
    };

    return (
        <React.Fragment>
            <BreadcrumbItem
                mainTitle={t("group.groups")}
                subTitle={groupId ? t("group.updateGroup") : t("group.createGroup")}
            />

            <Form onSubmit={handleSubmit(onSubmit)}>
                <Card>
                    <Card.Header>
                        <h5>{t("group.groupInformation")}</h5>
                    </Card.Header>
                    <Card.Body>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t("group.name")}</Form.Label>
                                    <Controller
                                        name="name"
                                        control={control}
                                        render={({ field }) => (
                                            <Form.Control
                                                {...field}
                                                placeholder={t("group.namePlaceholder")}
                                                isInvalid={!!errors.name?.message}
                                            />
                                        )}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.name?.message && t(errors.name.message as string)}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t("group.users")}</Form.Label>
                                    <Controller
                                        name="userIds"
                                        control={control}
                                        render={({ field }) => (
                                            <AsyncSelect
                                                isMulti
                                                cacheOptions
                                                defaultOptions={defaultUserOptions}
                                                loadOptions={debouncedLoadUsers}
                                                value={defaultUserOptions.filter((opt) =>
                                                    field.value?.includes(opt.value),
                                                )}
                                                onChange={(selected) => {
                                                    const ids = selected
                                                        ? (selected as ISelectOption<number>[]).map((s) => s.value)
                                                        : [];
                                                    field.onChange(ids);
                                                }}
                                                placeholder={t("group.selectUsers")}
                                                styles={customStyles}
                                                menuPortalTarget={document.body}
                                            />
                                        )}
                                    />
                                    {errors.userIds?.message && (
                                        <div className="invalid-feedback d-block">
                                            {t(errors.userIds.message as string)}
                                        </div>
                                    )}
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t("group.institutions")}</Form.Label>
                                    <Controller
                                        name="institutionIds"
                                        control={control}
                                        render={({ field }) => (
                                            <AsyncSelect
                                                isMulti
                                                cacheOptions
                                                defaultOptions={defaultInstitutionOptions}
                                                loadOptions={debouncedLoadInstitutions}
                                                value={defaultInstitutionOptions.filter((opt) =>
                                                    field.value?.includes(opt.value),
                                                )}
                                                onChange={(selected) => {
                                                    const ids = selected
                                                        ? (selected as ISelectOption<number>[]).map((s) => s.value)
                                                        : [];
                                                    field.onChange(ids);
                                                }}
                                                placeholder={t("group.selectInstitutions")}
                                                styles={customStyles}
                                                menuPortalTarget={document.body}
                                            />
                                        )}
                                    />
                                    {errors.institutionIds?.message && (
                                        <div className="invalid-feedback d-block">
                                            {t(errors.institutionIds.message as string)}
                                        </div>
                                    )}
                                </Form.Group>
                            </Col>
                        </Row>
                    </Card.Body>
                    <Card.Footer className="text-end">
                        <Button
                            variant="light"
                            className="me-2"
                            onClick={() => navigate(-1)}
                        >
                            {t("button.cancel")}
                        </Button>
                        <Button type="submit" variant="primary" disabled={isSubmitting}>
                            {isSubmitting ? t("button.submitting") : t("button.save")}
                        </Button>
                    </Card.Footer>
                </Card>
            </Form>
        </React.Fragment>
    );
};

export default GroupForm;
